/**
 * Los campos que no son plata: cantidades, casillas y el buscador de producto.
 *
 * Estan aca y no en `components/ui/` porque son de este modulo: una cantidad de
 * stock no es un importe (no lleva moneda, no lleva simbolo) pero comparte con
 * `<InputImporte />` la regla que importa — nunca emitir un 0 silencioso cuando
 * lo que se tipeo no se entiende.
 */
import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from "react";
import { formatearNumero, parsearImporte } from "@tora/shared";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { aNumero, type Producto } from "@/lib/tipos";

import { filtrarPorTexto } from "./logica";
import { nombreCompleto } from "./nombre";

// ─────────────────────────────────────────────────────────────────────────────
// Envoltorio de campo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Etiqueta + control + ayuda. Solo para no repetir el mismo bloque diez veces.
 *
 * No va sobre `ui/campo`: ese asocia label y control clonando al hijo con un
 * `id`, y aca el hijo es muchas veces un `<Select>` de Radix, cuyo Root no
 * renderiza nada. El `id` explicito apunta al `SelectTrigger`, que es el que
 * existe en el DOM.
 */
export function Campo({
  label,
  id,
  ayuda,
  requerido = false,
  children,
}: {
  label: string;
  id: string;
  ayuda?: string;
  requerido?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} requerido={requerido}>
        {label}
      </Label>
      {children}
      {ayuda ? <p className="text-xs text-muted-foreground">{ayuda}</p> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cantidad
// ─────────────────────────────────────────────────────────────────────────────

export interface CampoCantidadProps {
  label: string;
  valor: number | null;
  onValorChange: (v: number | null) => void;
  /** Se muestra pegado al campo: "%", "u.", etc. */
  sufijo?: string;
  ayuda?: string;
  placeholder?: string;
  requerido?: boolean;
  /**
   * Error puesto DESDE AFUERA (el formulario validó y este campo es el que
   * falta). Convive con el error propio del parseo: el de adentro gana, porque
   * "no se entiende como número" es más inmediato que "falta este dato".
   */
  error?: string;
  autoFocus?: boolean;
  id?: string;
  className?: string;
}

/**
 * Un numero que no es plata.
 *
 * No usa `type="number"`: la rueda del mouse cambia el valor por accidente y el
 * formato argentino (coma decimal) no entra. El parseo es el mismo
 * `parsearImporte()` de `@tora/shared` que usa el backend, para que "1.234,5" y
 * "1234.5" signifiquen lo mismo en toda la aplicacion.
 */
/**
 * Un entero se muestra entero: «100» ml, «15.000» puffs, «2» u. Con decimales
 * fijos todo decia «100,00», y una cantidad de mililitros con centavos se lee
 * como plata. Si el numero trae decimales, se muestran.
 */
export function mostrarCantidad(valor: number): string {
  return formatearNumero(valor, Number.isInteger(valor) ? 0 : 2);
}

export function CampoCantidad({
  label,
  valor,
  onValorChange,
  sufijo,
  ayuda,
  placeholder = "0",
  requerido = false,
  error: errorExterno,
  autoFocus = false,
  id,
  className,
}: CampoCantidadProps): JSX.Element {
  const idAuto = useId();
  const campoId = id ?? `cantidad-${idAuto}`;
  const errorId = `${campoId}-error`;

  const [texto, setTexto] = useState<string>(() => (valor === null ? "" : mostrarCantidad(valor)));
  const [errorPropio, setErrorPropio] = useState<string | null>(null);
  const error = errorPropio ?? errorExterno ?? null;
  const editando = useRef(false);

  // Si el valor cambia desde AFUERA, el campo lo tiene que mostrar. Sin esto
  // escribia bien pero no leia: al despejar el margen desde el precio de venta,
  // el porcentaje se guardaba nuevo y en pantalla seguia el viejo, que es la
  // peor version posible —el numero correcto guardado, el equivocado a la
  // vista. Mientras la persona tipea no se pisa lo que escribio.
  useEffect(() => {
    if (editando.current) return;
    setTexto(valor === null ? "" : mostrarCantidad(valor));
    setErrorPropio(null);
  }, [valor]);

  function alCambiar(e: ChangeEvent<HTMLInputElement>): void {
    const nuevo = e.target.value;
    editando.current = true;
    setTexto(nuevo);

    if (nuevo.trim() === "") {
      setErrorPropio(requerido ? "Falta este dato." : null);
      onValorChange(null);
      return;
    }
    const parseado = parsearImporte(nuevo);
    if (parseado === null) {
      setErrorPropio("No se entiende como número.");
      onValorChange(null); // null explicito, nunca 0.
      return;
    }
    setErrorPropio(null);
    onValorChange(parseado);
  }

  const hayError = error !== null;

  // Tampoco va sobre `ui/campo`: el sufijo necesita un envoltorio `relative`
  // alrededor del input, y el primitivo clonaria ese envoltorio en vez del
  // control. La asociacion se hace a mano con las mismas reglas: `htmlFor`,
  // `aria-invalid`, `aria-describedby` y el error con `role="alert"`.
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={campoId} requerido={requerido}>
        {label}
      </Label>
      <div className="relative">
        <Input
          id={campoId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          autoFocus={autoFocus}
          value={texto}
          placeholder={placeholder}
          aria-invalid={hayError}
          aria-describedby={hayError ? errorId : undefined}
          onChange={alCambiar}
          onFocus={() => {
            editando.current = true;
          }}
          onBlur={() => {
            editando.current = false;
            if (valor !== null) setTexto(mostrarCantidad(valor));
          }}
          className={cn("text-right font-medium tabular-nums", sufijo && "pr-8")}
        />
        {sufijo ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
          >
            {sufijo}
          </span>
        ) : null}
      </div>
      {hayError ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : ayuda ? (
        <p className="text-xs text-muted-foreground">{ayuda}</p>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Casilla
// ─────────────────────────────────────────────────────────────────────────────

/** Una casilla con fila-objetivo: tocar el texto tilda, y en el celular mide 44px. */
export function Casilla({
  label,
  ayuda,
  marcado,
  onMarcadoChange,
  id,
}: {
  label: string;
  ayuda?: string;
  marcado: boolean;
  onMarcadoChange: (v: boolean) => void;
  id?: string;
}): JSX.Element {
  const idAuto = useId();
  const campoId = id ?? `casilla-${idAuto}`;

  return (
    <Checkbox
      id={campoId}
      label={label}
      ayuda={ayuda}
      checked={marcado}
      onChange={(e) => onMarcadoChange(e.target.checked)}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Buscador de producto
// ─────────────────────────────────────────────────────────────────────────────

export interface SelectorProductoProps {
  productos: ReadonlyArray<Producto>;
  seleccionado: Producto | null;
  onSeleccionar: (p: Producto) => void;
  label?: string;
  autoFocus?: boolean;
  /**
   * Por defecto si: un movimiento de stock sin producto no existe. En una
   * oferta no, porque un combo puede no salir de un producto del catalogo.
   */
  requerido?: boolean;
  /** Lo que se va escribiendo: el Ingreso lo usa para precargar el alta. */
  onTexto?: (texto: string) => void;
}

/**
 * Elegir el producto de un movimiento de stock.
 *
 * No es un `<Select>`: un desplegable de cuarenta productos ya se recorre a
 * ojo, y de cuatrocientos no se recorre. Se busca por nombre o por codigo (el
 * interno y el del proveedor), que es como esta escrito en la caja de la
 * mercaderia que se tiene en la mano.
 *
 * Al lado de cada resultado va el stock actual: si hay dos productos parecidos,
 * ese numero suele ser lo que desempata cual es el que se esta cargando.
 */
export function SelectorProducto({
  productos,
  seleccionado,
  onSeleccionar,
  label = "Producto",
  autoFocus = false,
  requerido = true,
  onTexto,
}: SelectorProductoProps): JSX.Element {
  const idAuto = useId();
  const campoId = `producto-${idAuto}`;
  const [texto, setTextoLocal] = useState("");
  const setTexto = (t: string): void => {
    setTextoLocal(t);
    onTexto?.(t);
  };

  const resultados = useMemo(
    () => (texto.trim() ? filtrarPorTexto(productos, texto).slice(0, 8) : []),
    [productos, texto],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={campoId} requerido={requerido}>
        {label}
      </Label>

      {seleccionado ? (
        <Card
          variant="muted"
          padding="none"
          className="flex items-center justify-between gap-3 px-3 py-2"
        >
          <div className="min-w-0">
            {/* Con el color, la memoria y la batería: hay fichas con el mismo
                nombre (tres «IPHONE 18 PRO 256 GB», cuatro usados iguales) y
                solo eso dice cuál se eligió. */}
            <p className="truncate text-sm font-medium">{nombreCompleto(seleccionado)}</p>
            <p className="truncate text-xs tabular-nums text-muted-foreground">
              {seleccionado.codigo_interno} · stock{" "}
              {mostrarCantidad(aNumero(seleccionado.stock_actual))}
            </p>
          </div>
          <Button
            type="button"
            variant="link"
            className="shrink-0 text-xs"
            onClick={() => setTexto("")}
          >
            Cambiar
          </Button>
        </Card>
      ) : null}

      {!seleccionado || texto ? (
        <InputBusqueda
          id={campoId}
          valor={texto}
          onValorChange={setTexto}
          etiqueta="Buscar producto"
          placeholder="Buscar por nombre o código"
          autoFocus={autoFocus}
        />
      ) : null}

      {resultados.length > 0 ? (
        <ul className="divide-y divide-border rounded-md border border-border sm:max-h-52 sm:overflow-y-auto">
          {/* Alto propio solo desde `sm`: en el celular la lista crece y se
              desliza el cuadro entero, sin una zona adentro de otra. */}
          {resultados.map((p) => (
            <li key={p.id}>
              {/* Cada resultado es una fila entera: alto libre, texto a la
                  izquierda y el ring adentro para que el scroll de la lista no
                  lo recorte. */}
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-between gap-3 whitespace-normal rounded-none px-3 py-2 text-left font-normal focus-visible:ring-inset focus-visible:ring-offset-0"
                onClick={() => {
                  onSeleccionar(p);
                  setTexto("");
                }}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm">{nombreCompleto(p)}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {p.codigo_interno}
                    {p.codigo_externo ? ` · ${p.codigo_externo}` : ""}
                  </span>
                </span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {mostrarCantidad(aNumero(p.stock_actual))} u.
                </span>
              </Button>
            </li>
          ))}
        </ul>
      ) : texto.trim() && !seleccionado ? (
        <p className="text-xs text-muted-foreground">
          No encontré ningún producto con «{texto.trim()}».
        </p>
      ) : null}
    </div>
  );
}
